/**
 * Classe helper pour générer les commandes ESC/POS
 * Compatible avec imprimantes EPSON TM-T20, TM-M30, etc.
 *
 * Référence: EPSON ESC/POS Command Reference
 */

export class ESCPOSCommands {
  // Commandes de base
  static readonly ESC = 0x1b;
  static readonly GS = 0x1d;
  static readonly LF = 0x0a; // Line Feed
  static readonly CR = 0x0d; // Carriage Return

  /**
   * Initialiser l'imprimante
   */
  static init(): Uint8Array {
    return new Uint8Array([this.ESC, 0x40]);
  }

  /**
   * Définir l'alignement
   */
  static setAlign(align: 'left' | 'center' | 'right'): Uint8Array {
    const value = align === 'left' ? 0 : align === 'center' ? 1 : 2;
    return new Uint8Array([this.ESC, 0x61, value]);
  }

  /**
   * Définir le texte en gras
   */
  static setBold(enabled: boolean): Uint8Array {
    return new Uint8Array([this.ESC, 0x45, enabled ? 1 : 0]);
  }

  /**
   * Définir le soulignement
   */
  static setUnderline(enabled: boolean): Uint8Array {
    return new Uint8Array([this.ESC, 0x2d, enabled ? 1 : 0]);
  }

  /**
   * Définir la taille du texte
   * size: 0 = normal, 1 = double hauteur, 2 = double largeur, 3 = double hauteur et largeur
   */
  static setTextSize(
    size: 'small' | 'normal' | 'large' | 'xlarge'
  ): Uint8Array {
    let value = 0x00;
    switch (size) {
      case 'small':
        value = 0x00;
        break;
      case 'normal':
        value = 0x00;
        break;
      case 'large':
        value = 0x11; // Double hauteur et largeur
        break;
      case 'xlarge':
        value = 0x22; // Triple hauteur et largeur
        break;
    }
    return new Uint8Array([this.GS, 0x21, value]);
  }

  /**
   * Imprimer du texte
   */
  static text(content: string): Uint8Array {
    const encoder = new TextEncoder();
    return encoder.encode(content);
  }

  /**
   * Saut de ligne
   */
  static lineFeed(lines: number = 1): Uint8Array {
    const commands: number[] = [];
    for (let i = 0; i < lines; i++) {
      commands.push(this.LF);
    }
    return new Uint8Array(commands);
  }

  /**
   * Ligne de séparation
   */
  static separator(char: string = '-', length: number = 42): Uint8Array {
    const line = char.repeat(length);
    return this.text(line + '\n');
  }

  /**
   * Couper le papier
   */
  static cut(): Uint8Array {
    // GS V m (m=1 = partial cut)
    return new Uint8Array([this.GS, 0x56, 0x01]);
  }

  /**
   * Ouvrir le tiroir-caisse
   * pin: 0 = pin 2, 1 = pin 5
   */
  static openCashDrawer(pin: 0 | 1 = 0): Uint8Array {
    // ESC p m t1 t2
    const m = pin === 0 ? 0 : 1;
    return new Uint8Array([this.ESC, 0x70, m, 0x32, 0xfa]);
  }

  /**
   * Code-barres (format CODE39)
   */
  static barcode(data: string, height: number = 50): Uint8Array {
    const commands: number[] = [];

    // Définir hauteur du code-barres
    commands.push(this.GS, 0x68, height);

    // Définir largeur du module (1-6)
    commands.push(this.GS, 0x77, 2);

    // Imprimer texte HRI en dessous
    commands.push(this.GS, 0x48, 2);

    // Imprimer code-barres CODE39
    commands.push(this.GS, 0x6b, 0x04);

    // Ajouter les données
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(data);
    commands.push(...Array.from(dataBytes));

    // Terminer
    commands.push(0x00);

    return new Uint8Array(commands);
  }

  /**
   * QR Code
   */
  static qrcode(data: string, size: number = 6): Uint8Array {
    const commands: number[] = [];
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(data);

    // Sélectionner modèle QR Code
    commands.push(this.GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);

    // Définir taille du module (1-16)
    commands.push(this.GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, size);

    // Définir niveau de correction d'erreur (L=48, M=49, Q=50, H=51)
    commands.push(this.GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31);

    // Stocker les données
    const len = dataBytes.length + 3;
    const pL = len & 0xff;
    const pH = (len >> 8) & 0xff;
    commands.push(this.GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30);
    commands.push(...Array.from(dataBytes));

    // Imprimer
    commands.push(this.GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30);

    return new Uint8Array(commands);
  }

  /**
   * Combiner plusieurs commandes
   */
  static combine(...commands: Uint8Array[]): Uint8Array {
    const totalLength = commands.reduce((sum, cmd) => sum + cmd.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const cmd of commands) {
      result.set(cmd, offset);
      offset += cmd.length;
    }
    return result;
  }
}
